/* eslint-disable no-undef */
import throttle from 'lodash.throttle'
import log from './utils/logger.js'
import {HTML_IDS} from './constants.js'
import {
    addBannerDiv,
    getGameMinWidth,
    GAME_TARGET_ID,
    hideAd,
    showAd
} from './html-manager.js'

import {
    BANNER_ACTIVE,
    BANNER_INACTIVE,
    BANNER_REFRESHED,
    tracking,
    getBannerRefreshTrackedParameters
} from './tracking.js'

const GOOGLE_PUBLISHER_TAG =
    'https://securepubads.g.doubleclick.net/tag/js/gpt.js'

let isAdShown = false
let adPlaceholderWidth = 0
let widthObserver
let minGameWidth
let adSlot

const gameStyleWidth = () => {
    return isAdShown ? `calc(100% - ${adPlaceholderWidth}px)` : `100%`
}

let refreshTimeoutRef
const slotOnloadHandler = (refreshTime) => (event) => {
    const slotId = event.slot.getSlotElementId()
    log.debug('Creative iframe load event has fired.', slotId)

    refreshTimeoutRef = setTimeout(() => {
        googletag.pubads().refresh([event.slot])
        clearTimeout(refreshTimeoutRef)
    }, refreshTime * 1000)
}

const initGPTLogic = (refreshTime, adSlotSettings) => {
    window.googletag = window.googletag || {cmd: []}
    googletag.cmd.push(() => {
        // Define a fixed-size ad slot.
        // [START fixed_size_ad]
        adSlot = googletag
            .defineSlot(
                adSlotSettings.unitPath,
                adSlotSettings.size,
                HTML_IDS.AD_CONTAINER
            )
            .addService(googletag.pubads())

        googletag
            .pubads()
            .addEventListener('slotOnload', slotOnloadHandler(refreshTime))

        googletag.pubads().addEventListener('slotRenderEnded', (event) => {
            tracking(BANNER_REFRESHED, getBannerRefreshTrackedParameters(event))

            const slot = event.slot
            // Log details of the rendered ad.
            log.debug(
                'Slot',
                slot.getSlotElementId(),
                'finished rendering.',
                JSON.stringify(
                    {
                        'Advertiser ID': event.advertiserId,
                        'Campaign ID': event.campaignId,
                        'Creative ID': event.creativeId,
                        'Is empty?': event.isEmpty,
                        'Line Item ID': event.lineItemId,
                        Size: event.size,
                        'Source Agnostic Creative ID':
                            event.sourceAgnosticCreativeId,
                        'Source Agnostic Line Item ID':
                            event.sourceAgnosticLineItemId
                    },
                    null,
                    4
                )
            )
        })

        if (process.env.NODE_ENV !== 'production') {
            googletag
                .pubads()
                .addEventListener('impressionViewable', (event) => {
                    const slotId = event.slot.getSlotElementId()
                    log.debug('Impression has become viewable.', slotId)
                })
        }

        googletag.pubads().enableSingleRequest()
        googletag.enableServices()

        googletag.display(HTML_IDS.AD_CONTAINER)
    })
}

const pauseAd = () => {
    if (!isAdShown) return
    isAdShown = false

    log.debug(`pause banner ad`)
    const gameIFrame = document.querySelector(`#${GAME_TARGET_ID}`)
    gameIFrame.style.width = gameStyleWidth()

    clearTimeout(refreshTimeoutRef)
    hideAd()

    tracking(BANNER_INACTIVE)
}

const resumeAd = () => {
    if (isAdShown) return
    isAdShown = true

    log.debug(`resume banner ad`)
    const gameIFrame = document.querySelector(`#${GAME_TARGET_ID}`)
    gameIFrame.style.width = gameStyleWidth()

    showAd()
    googletag.pubads().refresh([adSlot])

    tracking(BANNER_ACTIVE)
}

// Callback function to execute when mutations are observed. Game.style.width in our case
const widthObserverCallback = function (mutationsList) {
    // Use traditional 'for loops' for IE 11
    for (const mutation of mutationsList) {
        if (mutation.target.style.width !== gameStyleWidth()) {
            mutation.target.style.width = gameStyleWidth()
        }
    }
}

const onResizeHandler = () => {
    if (window.innerWidth < minGameWidth + adPlaceholderWidth) {
        pauseAd()
    } else {
        resumeAd()
    }
}

const throttledOnResizeHandler = throttle(onResizeHandler, 100)

const startObserverForGameWidth = () => {
    // Select the node that will be observed for mutations
    const gameIFrame = document.querySelector(`#${GAME_TARGET_ID}`)

    // Options for the observer (which mutations to observe)
    const config = {
        attributes: true,
        subtree: false,
        childList: false,
        attributeFilter: ['style']
    }
    // Start observing the target node for configured mutations
    widthObserver.observe(gameIFrame, config)

    onResizeHandler() // trigger the re-align of the banner
    window.addEventListener(`resize`, throttledOnResizeHandler)
}

const addGoogleTagLogic =
    ({refreshTime, adSlotSettings}) =>
    () => {
        log.debug('GPT script has been loaded')
        adPlaceholderWidth = adSlotSettings.placeholderSize.width

        addBannerDiv(adSlotSettings)
        minGameWidth = getGameMinWidth()
        widthObserver = new MutationObserver(widthObserverCallback)

        initGPTLogic(refreshTime, adSlotSettings)

        isAdShown = true

        startObserverForGameWidth()

        if (isAdShown) {
            tracking(BANNER_ACTIVE)
        }
    }

const initBannerAd = (config) => {
    try {
        if (googletag) {
            log.debug(`Google publisher tag was already initialized.`)
            enableAd()
        }
    } catch {
        log.debug(`Google publisher tag is loading...`)

        const script = document.createElement('script')
        script.addEventListener('load', addGoogleTagLogic(config))
        script.src = GOOGLE_PUBLISHER_TAG
        document.head.append(script)
    }
}

const enableAd = () => {
    log.info(`enable ad`)
    startObserverForGameWidth()
    resumeAd()
}

const disableAd = () => {
    log.info(`disable ad`)
    widthObserver?.disconnect()
    window.removeEventListener(`resize`, throttledOnResizeHandler)
    pauseAd()
}

export {initBannerAd, disableAd}
